import styled from '@emotion/styled';
import * as errorIcon from '~/assets/icons/icon_alert_error.svg';
import * as infoIcon from '~/assets/icons/icon_alert_info.svg';
import * as iconClose from '~/assets/icons/icon_close_gray.svg';
import * as successIcon from '~/assets/icons/icon_success.svg';
import * as warningIcon from '~/assets/icons/question_circle_yellow.svg';
import CloseIcon from '~/components/CloseIcon';
import FlexContainer, { HorizontalAlign } from '~/components/Layout/FlexContainer';
import * as React from 'react';
import { colors, fontWeight } from '~/styles/constants';

interface Props {
  withIcon?: boolean;
  hideBorderRadius?: boolean;
  onClose?: (evt?: React.MouseEvent) => void;
  closeIconPendoTag?: string;
  children: React.ReactNode;
}

const MessageContainer = styled(FlexContainer)`
  flex-grow: 1;
  padding: 8px 0;
`;

const MessageContent = styled('div')<{ hideBorderRadius?: boolean }>`
  border: 1px solid ${colors.macaroni300};
  ${(props) =>
    props.hideBorderRadius
      ? ''
      : `border-top-right-radius: 0.25rem; 
      border-bottom-right-radius: 0.25rem`};

  display: flex;
  flex-direction: row;
  margin-bottom: 10px;
  padding: 0.75rem 1.3rem;
  background: #fffeef linear-gradient(${colors.macaroni300} 100%, rgba(255, 255, 255, 0.001) 0)
    no-repeat;
  background-size: 3px 100%;

  font-size: 13px;
  line-height: 20px;
  font-weight: 300;

  & p {
    margin: 0;
  }

  & strong {
    font-weight: ${fontWeight.medium};
  }

  & ul {
    padding-left: 15px;
    list-style: disc;
    margin: 5px;
  }
`;

const ErrorMessageContent = styled(MessageContent)`
  border: 1px solid ${colors.form.errorBorder};
  background: ${colors.form.errorBackground}
    linear-gradient(${colors.form.errorBar} 100%, rgba(255, 255, 255, 0.001) 0) no-repeat;
  background-size: 3px 100%;
  color: ${colors.gray1000};
`;

const SuccessMessageContent = styled(MessageContent)`
  border: 1px solid ${colors.form.successBorder};
  background: ${colors.form.successBackground}
    linear-gradient(${colors.form.successBar} 100%, rgba(255, 255, 255, 0.001) 0) no-repeat;
  background-size: 3px 100%;
`;

const WarningMessageContent = styled(MessageContent)`
  border: 1px solid ${colors.form.warningBorder};
  background: ${colors.form.warningBackground};
  padding: 12px 0px 12px 12px;
  margin: 16px 0;
  box-sizing: border-box;
  border-radius: 6px;
  color: ${colors.gray1000};
  font-size: 13px;
  line-height: 20px;
`;

const IconContainer = styled('div')`
  height: 28px;
  width: 28px;
  background-color: ${colors.white};
  box-shadow: 0 2px 4px 0 ${colors.gray50};

  display: flex;
  flex-direction: column;
  justify-content: center;
  border-radius: 50%;
`;

const AlertIcon = styled('img')`
  width: 28px;
  height: 14px;
`;

const SuccessIcon = styled('img')`
  width: 28px;
  height: 26px;
`;

const WarningIcon = styled('img')`
  margin-right: 12px;
`;

const MessageTextContainer = styled('div')`
  margin-left: 13px;
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const CloseIconContainer = styled('div')<Props>`
  padding-left: 20px;
  padding-top: ${(props: Props) => (props.withIcon ? '13px' : '8px')};
`;

const Message = (props: Props) => {
  return (
    <MessageContent hideBorderRadius={props.hideBorderRadius}>
      <MessageContainer horizontalAlign={HorizontalAlign.Start}>
        {props.withIcon && (
          <IconContainer>
            <AlertIcon src={infoIcon} alt="info" />
          </IconContainer>
        )}
        <MessageTextContainer>{props.children}</MessageTextContainer>
      </MessageContainer>
      {props.onClose && (
        <CloseIconContainer withIcon={props.withIcon}>
          <CloseIcon
            onClose={props.onClose}
            data-pendo={props.closeIconPendoTag}
            iconSvg={iconClose}
          />
        </CloseIconContainer>
      )}
    </MessageContent>
  );
};

export const WarningMessage = (props: Props) => {
  return (
    <WarningMessageContent hideBorderRadius={props.hideBorderRadius}>
      {props.withIcon && <WarningIcon src={warningIcon} alt="warning" />}
      <div>{props.children}</div>
      {props.onClose && (
        <CloseIconContainer withIcon={props.withIcon}>
          <CloseIcon
            onClose={props.onClose}
            data-pendo={props.closeIconPendoTag}
            iconSvg={iconClose}
          />
        </CloseIconContainer>
      )}
    </WarningMessageContent>
  );
};

export const ErrorMessage = (props: Props) => {
  return (
    <ErrorMessageContent hideBorderRadius={props.hideBorderRadius}>
      <MessageContainer horizontalAlign={HorizontalAlign.Start}>
        {props.withIcon && (
          <IconContainer>
            <AlertIcon src={errorIcon} alt="error" />
          </IconContainer>
        )}
        <MessageTextContainer>{props.children}</MessageTextContainer>
      </MessageContainer>
      {props.onClose && (
        <CloseIconContainer withIcon={props.withIcon}>
          <CloseIcon
            onClose={props.onClose}
            data-pendo={props.closeIconPendoTag}
            iconSvg={iconClose}
          />
        </CloseIconContainer>
      )}
    </ErrorMessageContent>
  );
};

export const SuccessMessage = (props: Props) => {
  return (
    <SuccessMessageContent hideBorderRadius={props.hideBorderRadius}>
      <MessageContainer horizontalAlign={HorizontalAlign.Start}>
        {props.withIcon && (
          <IconContainer>
            <SuccessIcon src={successIcon} alt="success" />
          </IconContainer>
        )}
        <MessageTextContainer>{props.children}</MessageTextContainer>
      </MessageContainer>
      {props.onClose && (
        <CloseIconContainer withIcon={props.withIcon}>
          <CloseIcon
            onClose={props.onClose}
            data-pendo={props.closeIconPendoTag}
            iconSvg={iconClose}
          />
        </CloseIconContainer>
      )}
    </SuccessMessageContent>
  );
};

export default Message;
